import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {
  ActionEvent,
  NgxAurMatTableModule,
  TableRow,
} from 'ngx-aur-mat-table';

import {MaterialModule} from '@common-module/material.module';
import {DriverView} from '@common-module/modelinterfaces/driver-view.model';

import {
  createDriverListTableConfig,
  DELETE_DRIVER_ACTION,
  OPEN_DRIVER_HISTORY_ACTION,
} from './driver-list-table.config';

@Component({
  selector: 'app-driver-list-table',
  imports: [
    MaterialModule,
    NgxAurMatTableModule,
    TranslateModule,
  ],
  templateUrl: './driver-list-table.component.html',
  styleUrl: './driver-list-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DriverListTableComponent {
  public readonly drivers = input.required<DriverView[]>();
  public readonly isListMode = input.required<boolean>();
  public readonly canEdit = input.required<boolean>();
  public readonly lastSelectedUnitName = input<string>();

  public readonly driverEdit = output<DriverView>();
  public readonly driverHistoryOpen = output<DriverView>();
  public readonly driverAssign = output<DriverView>();
  public readonly driverDelete = output<DriverView>();

  private readonly translateService = inject(TranslateService);
  private readonly tableContainer = viewChild<ElementRef<HTMLElement>>('tableContainer');

  protected readonly expandedDriver = signal<DriverView | null>(null);
  protected readonly highlightedDriver = signal<DriverView | null>(null);

  protected readonly tableConfig = computed(() => createDriverListTableConfig(
    key => this.translateService.instant(key),
    this.isListMode(),
    this.canEdit(),
  ));

  protected onRowClick(driver: DriverView | undefined): void {
    if (!this.canEdit()) {
      return;
    }
    if (!driver) {
      this.setExpandedDriver(null);
      return;
    }
    if (this.isListMode()) {
      this.driverEdit.emit(driver);
      return;
    }
    const wasExpanded = this.expandedDriver() === driver;
    this.toggleExpandedRow(driver);
    if (!wasExpanded) {
      this.scrollExpandedRowIntoView();
    }
  }

  protected onRowAction(event: ActionEvent<DriverView>): void {
    switch (event.action) {
      case OPEN_DRIVER_HISTORY_ACTION:
        this.driverHistoryOpen.emit(event.value);
        break;
      case DELETE_DRIVER_ACTION:
        this.toggleExpandedRow(event.value);
        break;
    }
  }

  protected cancel(): void {
    this.setExpandedDriver(null);
  }

  protected assign(driver: DriverView): void {
    this.driverAssign.emit(driver);
  }

  protected delete(driver: DriverView): void {
    this.driverDelete.emit(driver);
  }

  protected asDriver(row: TableRow<DriverView>): DriverView {
    return row.rowSrc;
  }

  protected unitIcon(driver: DriverView): string {
    return driver.unit ? 'insert_link' : 'link_off';
  }

  protected onHighlightedDriverChange(driver: DriverView | null): void {
    this.highlightedDriver.set(driver);
  }

  private toggleExpandedRow(driver: DriverView): void {
    this.setExpandedDriver(
      this.expandedDriver() === driver ? null : driver,
    );
  }

  private setExpandedDriver(driver: DriverView | null): void {
    this.expandedDriver.set(driver);
    this.highlightedDriver.set(driver);
  }

  private scrollExpandedRowIntoView(): void {
    setTimeout(() => {
      this.tableContainer()?.nativeElement.scrollBy({
        top: 85,
        behavior: 'smooth',
      });
    }, 500);
  }
}
